
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Mic, 
  MicOff, 
  Video, 
  VideoOff, 
  Phone, 
  PhoneOff,
  Settings, 
  Users, 
  MessageSquare, 
  Send,
  Monitor,
  MonitorOff
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface VideoRoomProps {
  appointmentId: string;
  userId: string;
  role: 'patient' | 'psychologist';
  onClose?: () => void;
}

interface ChatMessage {
  id: string;
  sender: string;
  senderRole: 'patient' | 'psychologist';
  text: string;
  timestamp: Date;
}

const VideoRoom: React.FC<VideoRoomProps> = ({ 
  appointmentId, 
  userId,
  role,
  onClose 
}) => {
  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState<any>(null);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [participants, setParticipants] = useState<{id: string, role: string, name: string}[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeTab, setActiveTab] = useState('video');
  
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);

  useEffect(() => {
    loadAppointmentData();
    initializeMediaDevices();
    setupRealtimeSubscription();

    return () => {
      cleanup();
    };
  }, [appointmentId, userId]);

  const loadAppointmentData = async () => {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          id, date, time, duration, status,
          psychologists(name),
          profiles(first_name, last_name)
        `)
        .eq('id', appointmentId)
        .single();

      if (error) throw error;
      
      setAppointment(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading appointment:', error);
      toast.error('Erro ao carregar dados da consulta');
    }
  };

  const initializeMediaDevices = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      setIsCallActive(true);
    } catch (error) {
      console.error('Error accessing media devices:', error);
      toast.error('Erro ao acessar câmera e microfone');
    }
  };

  const setupRealtimeSubscription = () => {
    // Aqui você implementaria a comunicação em tempo real
    // com WebRTC ou outra solução de videochamada
  };

  const toggleMicrophone = () => {
    if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !isMicOn;
      });
      setIsMicOn(!isMicOn);
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTracks = localStreamRef.current.getVideoTracks();
      videoTracks.forEach(track => {
        track.enabled = !isVideoOn;
      });
      setIsVideoOn(!isVideoOn);
    }
  };

  const toggleScreenShare = async () => {
    try {
      if (!isScreenSharing) {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true
        });
        
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = screenStream;
        }
        
        setIsScreenSharing(true);
        
        screenStream.getVideoTracks()[0].addEventListener('ended', () => {
          setIsScreenSharing(false);
          initializeMediaDevices();
        });
      } else {
        setIsScreenSharing(false);
        initializeMediaDevices();
      }
    } catch (error) {
      console.error('Error sharing screen:', error);
      toast.error('Erro ao compartilhar tela');
    }
  };

  const sendMessage = () => {
    if (!newMessage.trim()) return;

    const message: ChatMessage = {
      id: Date.now().toString(),
      sender: role === 'psychologist' ? appointment?.psychologists?.name : 
             `${appointment?.profiles?.first_name} ${appointment?.profiles?.last_name}`,
      senderRole: role,
      text: newMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, message]);
    setNewMessage('');
  };

  const endCall = () => {
    cleanup();
    toast.success('Chamada encerrada');
    if (onClose) onClose();
  };

  const cleanup = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Conectando à videochamada...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Header */}
      <div className="bg-white border-b p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold">
              Consulta - {format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })}
            </h1>
            <p className="text-sm text-muted-foreground">
              {role === 'psychologist' ? 
                `Paciente: ${appointment?.profiles?.first_name} ${appointment?.profiles?.last_name}` :
                `Psicólogo: ${appointment?.psychologists?.name}`
              }
            </p>
          </div>
          <Badge className="bg-green-100 text-green-800">
            Em andamento
          </Badge>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Video Area */}
        <div className="flex-1 relative">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
            <TabsList className="absolute top-4 left-4 z-10 bg-black/50">
              <TabsTrigger value="video" className="text-white">
                <Video className="h-4 w-4 mr-2" />
                Vídeo
              </TabsTrigger>
              <TabsTrigger value="participants" className="text-white">
                <Users className="h-4 w-4 mr-2" />
                Participantes
              </TabsTrigger>
            </TabsList>

            <TabsContent value="video" className="h-full m-0">
              <div className="relative h-full">
                {/* Remote Video */}
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover bg-gray-800"
                />
                
                {/* Local Video */}
                <div className="absolute bottom-4 right-4 w-48 h-36 border-2 border-white rounded-lg overflow-hidden">
                  <video
                    ref={localVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover bg-gray-700"
                  />
                </div>

                {/* No video placeholder */}
                {!isVideoOn && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                    <div className="text-center text-white">
                      <VideoOff className="h-16 w-16 mx-auto mb-4 opacity-50" />
                      <p>Câmera desligada</p>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="participants" className="h-full m-0 p-4">
              <div className="text-white">
                <h3 className="text-lg font-semibold mb-4">Participantes (2)</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-2 rounded bg-white/10">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm">
                      {role === 'psychologist' ? 'P' : 'Ps'}
                    </div>
                    <span>
                      {role === 'psychologist' ? 
                        appointment?.psychologists?.name + ' (Você)' :
                        `${appointment?.profiles?.first_name} ${appointment?.profiles?.last_name} (Você)`
                      }
                    </span>
                  </div>
                  <div className="flex items-center gap-3 p-2 rounded bg-white/10">
                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center text-white text-sm">
                      {role === 'patient' ? 'P' : 'Ps'}
                    </div>
                    <span>
                      {role === 'patient' ? 
                        appointment?.psychologists?.name :
                        `${appointment?.profiles?.first_name} ${appointment?.profiles?.last_name}`
                      }
                    </span>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Chat Sidebar */}
        <div className="w-80 bg-white border-l flex flex-col">
          <div className="p-4 border-b">
            <h3 className="font-semibold flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Chat
            </h3>
          </div>
          
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-3">
              {messages.map((message) => (
                <div key={message.id} className="text-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-xs">
                      {message.sender}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {format(message.timestamp, 'HH:mm')}
                    </span>
                  </div>
                  <p className="bg-muted p-2 rounded">{message.text}</p>
                </div>
              ))}
            </div>
          </ScrollArea>
          
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Digite sua mensagem..."
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              />
              <Button size="sm" onClick={sendMessage}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-gray-800 p-4">
        <div className="flex items-center justify-center gap-4">
          <Button
            variant={isMicOn ? "secondary" : "destructive"}
            size="lg"
            onClick={toggleMicrophone}
            className="rounded-full w-12 h-12 p-0"
          >
            {isMicOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
          </Button>
          
          <Button
            variant={isVideoOn ? "secondary" : "destructive"}
            size="lg"
            onClick={toggleVideo}
            className="rounded-full w-12 h-12 p-0"
          >
            {isVideoOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
          </Button>
          
          <Button
            variant={isScreenSharing ? "default" : "secondary"}
            size="lg"
            onClick={toggleScreenShare}
            className="rounded-full w-12 h-12 p-0"
          >
            {isScreenSharing ? <MonitorOff className="h-5 w-5" /> : <Monitor className="h-5 w-5" />}
          </Button>
          
          <Button
            variant="destructive"
            size="lg"
            onClick={endCall}
            className="rounded-full w-12 h-12 p-0"
          >
            <PhoneOff className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VideoRoom;
